import { Divider, Typography } from "@mui/material"
import { alpha, Theme } from "@mui/system"
import { translate } from "app/language/service"
import { IRootState } from "app/model"
import { HOME_PATH, LOGIN_PATH } from "page/model"
import { navigate } from "page/tools/navigate"
import React, { useEffect } from "react"
import { connect } from "react-redux"
import { FlexBox } from "ui/components/FlexBox"
import { Logo } from "ui/components/Logo"
import { getUiConfigLoginImageSrc } from "ui/configuration/tools/common"
import { MAX_TABLET_WIDTH_IN_PX } from "ui/model"
import { SIGN_UP_PAGE_ID } from "user/model"
import { isLoggedIn } from "user/tools/common"
import { LoginPageShiningSquares } from "../LoginPageShiningSquares"
import { SignUpWizard } from "./SignUpWizard"

interface ISignUpPageProps {
  isLoggedIn: boolean
}

const SignUpPageFn = ({ isLoggedIn }: ISignUpPageProps) => {
  useEffect(() => {
    if (isLoggedIn) {
      navigate(HOME_PATH)
    }
  }, [])
  const backgroundSx: any = {}
  const imageSrc = getUiConfigLoginImageSrc()
  if (imageSrc) {
    backgroundSx.backgroundImage = `url(${imageSrc})`
    backgroundSx.backgroundRepeat = "no-repeat"
    backgroundSx.backgroundPosition = "center"
    backgroundSx.backgroundSize = "cover"
  } else {
    backgroundSx.backgroundColor = (theme: Theme) => theme.palette.primary.main
    backgroundSx.background = (theme: Theme) =>
      `linear-gradient(90deg, ${alpha(theme.palette.primary.main as string, 0.75)} 0%, ${
        theme.palette.primary.main
      } 100%)`
  }
  return (
    <FlexBox
      id={SIGN_UP_PAGE_ID}
      vertical
      fullWidth
      sx={{
        "position": "relative",
        "flexDirection": "column",
        "height": "100%",
        "padding": 0,
        "margin": 0,
        "boxShadow": "0 0.125rem 0.5rem 0 rgba(0, 0, 0, 0.2)",
        "backgroundColor": theme => theme.palette.background.paper,
        "& .code-version": {
          marginBottom: "1rem"
        }
      }}>
      {!imageSrc && <LoginPageShiningSquares />}
      <FlexBox
        sx={{
          ...backgroundSx,
          height: "calc(100% - 10.5rem)",
          minHeight: "calc(100% - 10.5rem)",
          maxHeight: "calc(100% - 10.5rem)",
          width: "calc(100% - 10.5rem)",
          minWidth: "calc(100% - 10.5rem)",
          maxWidth: "calc(100% - 10.5rem)",
          overflow: "hidden",
          [`@media (max-width: ${MAX_TABLET_WIDTH_IN_PX}px)`]: {
            height: "100%",
            minHeight: "100%",
            maxHeight: "100%",
            width: "100%",
            minWidth: "100%",
            maxWidth: "100%"
          }
        }}
        id={`${SIGN_UP_PAGE_ID}-background`}>
        <FlexBox
          gap=""
          vertical
          jc="space-evenly"
          sx={{
            "backgroundColor": theme => theme.palette.background.default,
            "borderRadius": ".375rem",
            "p": "2rem",
            "width": "60%",
            "minWidth": "60%",
            "maxWidth": "60%",
            "height": "calc(100% - 2rem)",
            "minHeight": "calc(100% - 2rem)",
            "maxHeight": "calc(100% - 2rem)",
            "pl": "3rem",
            "pr": "3rem",
            "zIndex": "1",
            "& .MuiFormControl-root": {
              borderRadius: ".375rem"
            },
            "& input": {
              px: ".4rem"
            },
            [`@media (max-width: ${MAX_TABLET_WIDTH_IN_PX}px)`]: {
              width: "100%",
              minWidth: "100%",
              maxWidth: "100%"
            }
          }}
          id={`${SIGN_UP_PAGE_ID}-form`}>
          <Logo id={`${SIGN_UP_PAGE_ID}-logo`} sx={{ minHeight: "5rem" }} />
          <FlexBox gap>
            <Typography
              id={`${SIGN_UP_PAGE_ID}-login-title`}
              color="textSecondary"
              onClick={() => navigate(LOGIN_PATH)}
              sx={{
                "textTransform": "uppercase",
                "&:hover": {
                  cursor: "pointer",
                  color: theme => theme.palette.primary.main
                }
              }}>
              {translate("login.signin")}
            </Typography>
            <Divider orientation="vertical" sx={{ height: "1rem" }} />
            <Typography
              id={`${SIGN_UP_PAGE_ID}-title`}
              color="textPrimary"
              sx={{
                textTransform: "uppercase"
              }}>
              {translate("login.signup")}
            </Typography>
          </FlexBox>
          <SignUpWizard />
        </FlexBox>
      </FlexBox>
    </FlexBox>
  )
}

export const SignUpPage = connect(
  (state: IRootState): ISignUpPageProps => ({
    isLoggedIn: isLoggedIn(state)
  })
)(SignUpPageFn)
